import { useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    UserProfileDTO,
    useDeleteUserProfileApi,
    useSaveUserProfileApi,
    useSearchUserProfileApi,
} from '../../../api/user-profile-api';
import SvgAdd from '../../../assets/icons/Add';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { N } from '../../../components/basic/n';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { LoadingButton } from '../../../components/loading-button';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveUserProfileModal } from './save-user-profile-modal';

export function UserProfileApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedUserProfile, setSelectedUserProfile] = useState<UserProfileDTO | undefined>();
    const profileInit = useSearchUserProfileApi();
    const saveProfile = useSaveUserProfileApi();
    const deleteProfile = useDeleteUserProfileApi();

    const { result, error, busy, initialized, initializing } = profileInit;

    const columns: IBasicTableColumn<UserProfileDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                width: 100,
                Cell: ({ row }) => <N>{row.id}</N>,
            },
            {
                Header: <T>username</T>,
                name: 'userName',
                width: 150,
            },
            {
                Header: <T>full-name</T>,
                name: 'fullName',
                width: 200,
            },
            {
                Header: <T>company</T>,
                name: 'company',
                width: 180,
            },
            {
                Header: <T>phone-number</T>,
                name: 'phoneNumber',
                width: 150,
            },
            {
                Header: <T>city</T>,
                name: 'city',
                width: 120,
            },
            {
                Header: <T>country</T>,
                name: 'country',
                width: 120,
            },
            {
                Header: <T>sms-notifications</T>,
                name: 'smsNotifications',
                width: 120,
                Cell: ({ row }) => <N>{row.smsNotifications ? '✓' : '✗'}</N>,
            },
        ],
        []
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        profileInit.post(null, {});
    }, [initialized, initializing, error, profileInit]);

    const handleDelete = (profile: UserProfileDTO) =>
        bd.msgbox(
            translate('delete-user-profile'),
            translate('delete-user-profile-confirm'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteProfile.remove(
                            { id: profile.id },
                            () => {
                                notify.dark(translate('deleted-successfully'));
                                setShowModal(false);
                                setSelectedUserProfile(undefined);
                                profileInit.post(null, {});
                                hide();
                            },
                            notify.error
                        );
                    }
                },
                { title: translate('cancel'), variant: 'outline', color: 'secondary' },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );

    const handleSave = (values: UserProfileDTO) => {
        saveProfile.post(
            null,
            values,
            () => {
                notify.success(translate('saved-successfully'));
                setShowModal(false);
                setSelectedUserProfile(undefined);
                profileInit.post(null, {});
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => profileInit.post(null, {})} />;

    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="user-profiles"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-user-profile</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedUserProfile(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.userProfiles}
                        onRowClick={({ row }) => {
                            setSelectedUserProfile(row);
                            setShowModal(true);
                        }}
                        hover
                    />

                    <SaveUserProfileModal
                        show={showModal}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedUserProfile={selectedUserProfile}
                        allowEdit={result.allowEdit}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedUserProfile(undefined);
                        }}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}

